/**
 * EVO — kunlik kurs ma'lumot qatlami.
 *
 * ============================================================================
 * MIJOZNING ANIQ TALABI
 * ============================================================================
 * «USD kursi kuniga bir marta kiritiladi va DASTUR TEPASIDA doim turadi.»
 *
 * Sabab — sotuv USD da, kassa so'mda, xarid CNY da (DATA_MODEL §1.4). Kurs
 * noto'g'ri bo'lsa kunlik barcha hujjat noto'g'ri hisoblanadi va KEYIN
 * QAYTA HISOBLANMAYDI: `daily_rates` yozuvi hujjatga muhrlanadi (§5).
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ MOCK OLIB TASHLANDI                                                       │
 * │                                                                           │
 * │ Bu fayl `lib/mock-rate.ts` ga tayanardi va shakl SERVERNIKIGA MOS         │
 * │ EMASDI: server `{ rateDate, rates: [...] }` qaytaradi, mock esa bitta     │
 * │ `{ currencyCode, rate }` obyektini. Endpoint 200 berardi, `rate.rate`     │
 * │ esa `undefined` bo'lardi — ya'ni vidjet BO'SH ko'rinardi va `isMock`      │
 * │ ogohlantirishi ham chiqmasdi, chunki so'rov muvaffaqiyatli edi.           │
 * │                                                                           │
 * │ Ya'ni mock «endpoint yo'q» holatini qoplashi kerak edi, amalda esa        │
 * │ endpoint BOR bo'lgani uchun u hech qachon ishlamadi va nomuvofiqlikni     │
 * │ yashirdi. Endi shakl serverdan olinadi, mock yo'q.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 */
import { api } from './api';
import { isToday } from './format';

/** Kurs saqlanadigan valyutalar. USD — baza, uning kursi saqlanmaydi. */
export type RateCurrency = 'UZS' | 'CNY';

export interface DailyRate {
  /** `1 USD = rate × currency`. SATR — kurs hech qachon float emas. */
  readonly currency: string;
  readonly rate: string;
  /** `YYYY-MM-DD` — QAYSI kunning kursi. Bugungidan farq qilishi mumkin. */
  readonly rateDate: string;
  /** Kurs muhrlangan: kun yopilgan, o'zgartirib bo'lmaydi. */
  readonly isLocked: boolean;
  /** Server hisoblagan: bu kurs bugungi emas. */
  readonly isStale: boolean;
}

/** `GET /api/fx/daily-rate` javobi. */
export interface DailyRateResponse {
  /** So'ralgan sana — kurslar undan ESKI bo'lishi mumkin. */
  readonly rateDate: string;
  readonly rates: readonly DailyRate[];
}

export type DailyRateStatus =
  /** Bugungi kurs kiritilgan — hammasi joyida. */
  | 'ok'
  /** Kurs bor, lekin bugungi emas — ogohlantirish kerak. */
  | 'stale'
  /** Umuman kurs yo'q. */
  | 'missing';

export interface DailyRateState {
  readonly status: DailyRateStatus;
  /** Sotuv va kassa uchun asosiy kurs. */
  readonly uzs: DailyRate | null;
  /** Xarid uchun — Xitoy zavodlari bilan hisob yuanda. */
  readonly cny: DailyRate | null;
  readonly rateDate: string;
}

export const DAILY_RATE_QUERY_KEY = ['fx', 'daily-rate'] as const;

/**
 * Kunlik kursni oladi.
 *
 * `RequirePermissions()` argumentsiz — ya'ni HAR autentifikatsiyalangan
 * foydalanuvchi kursni ko'radi. Bu ataylab: sotuvchi so'mdagi narxni
 * mijozga aytishi uchun kursni bilishi SHART, garchi uni o'zgartira
 * olmasa ham.
 */
export async function fetchDailyRate(): Promise<DailyRateState> {
  const response = await api.get<DailyRateResponse>('/fx/daily-rate');
  const find = (currency: RateCurrency): DailyRate | null =>
    response.rates.find((r) => r.currency === currency) ?? null;

  const uzs = find('UZS');

  /*
   * Holat FAQAT UZS bo'yicha aniqlanadi.
   *
   * Sabab: kunlik operatsiyalarning hammasi (sotuv, kassa, mijoz qarzi) shu
   * kursga bog'liq. CNY faqat konteyner kelganda kerak — u yo'q bo'lsa
   * xarid hujjati yaratilmaydi, lekin kunlik ish TO'XTAMAYDI. Ikkalasini
   * bitta ogohlantirishga qo'shish har kuni yolg'on signal berardi.
   */
  return {
    status: uzs === null ? 'missing' : isToday(uzs.rateDate) ? 'ok' : 'stale',
    uzs,
    cny: find('CNY'),
    rateDate: response.rateDate,
  };
}

/**
 * Kunlik kursni kiritadi yoki yangilaydi. Ruxsat: `fxrate.edit`.
 *
 * `rateDate` berilmasa server TOSHKENT sanasini oladi (UTC EMAS —
 * `@evo/core/datetime`). Bu farq muhim: mahalliy 00:00–05:00 oralig'ida
 * UTC kecha bo'ladi va kurs noto'g'ri kunga yozilardi.
 */
export function setDailyRate(input: {
  readonly currency: RateCurrency;
  readonly rate: string;
  readonly rateDate?: string;
}): Promise<DailyRate> {
  return api.post<DailyRate>('/fx/daily-rate', input);
}

/**
 * MARKAZIY BANKDAN KURSNI OLISH.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ ODAM QO'YGAN KURS USTIDAN YOZILMAYDI                                     ║
 * ║                                                                          ║
 * ║ Buxgalter kursni ataylab boshqacha qo'ygan bo'lishi mumkin (bank o'z     ║
 * ║ kursi bilan ishlaydi). Import uni jimgina qaytarib qo'ysa, ertalab       ║
 * ║ hech kim sababini topa olmasdi.                                          ║
 * ║                                                                          ║
 * ║ Server qoidasi: joriy kurs avvalgi MB importidagi qiymatga teng bo'lsa   ║
 * ║ — yangilanadi; farq qilsa — `kept` qaytadi va tegilmaydi.                ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export interface CbuImportLine {
  readonly currency: string;
  readonly rate: string;
  readonly status: 'created' | 'updated' | 'unchanged' | 'kept' | 'locked';
  /** `kept` bo'lganda — odam qo'ygan joriy kurs. */
  readonly current?: string;
}

export interface CbuImportResult {
  readonly rateDate: string;
  readonly source: 'cbu';
  readonly lines: readonly CbuImportLine[];
}

/** Ruxsat: `fxrate.edit`. `force` — odam qo'ygan kursni ham almashtirish. */
export function importCbuRates(force = false): Promise<CbuImportResult> {
  return api.post<CbuImportResult>('/fx/import-cbu', force ? { force: true } : {});
}
